import _ from 'lodash';

import { toQuery } from '@/utils/infrastructure';
import { request } from '@/utils/infrastructure/communication';
import {
  SESSION_TYPES,
  STORAGE_KEYS,
  getCurrentSession,
  getSessionItem,
  getStoredSessionContext,
  isSameContextId,
  setSessionItem,
} from '@/utils/infrastructure/auth';

/**
 * The cached profile is shared by every tab of the session, so it can describe
 * another tenant than the token this tab sends — a tab booting while a switch
 * elsewhere is still settling. Serving it would leave the profile on the
 * outgoing tenant, and the next switch back to it would look like no change.
 */
const matchesSessionTenant = (user) => {
  if (getCurrentSession()?.type !== SESSION_TYPES.AUTOMY) return true;
  const context = getStoredSessionContext();
  if (!context?.companyId) return true;
  if (!isSameContextId(user?.company?.id, context.companyId)) return false;
  return (
    !context.environmentId || isSameContextId(user?.defaultEnvironment?.id, context.environmentId)
  );
};

export async function queryCurrent(changeCompany) {
  let user = getSessionItem(STORAGE_KEYS.AUTH_USER);
  const cached = user && !user.includes('useCache') && !changeCompany ? JSON.parse(user) : null;
  if (cached && matchesSessionTenant(cached)) {
    let roles = cached?.roles;
    if (typeof roles === 'string') {
      try {
        roles = JSON.parse(roles);
      } catch {
        roles = null;
      }
    }
    if (Array.isArray(roles)) {
      return { ...cached, roles };
    }
    // roles missing or unparseable — bypass cache to fetch fresh data
  }

  const params = {
    select: 'id,isActive,fullName,names,lastNames,email,roles,defaultLanguage',
    expand: {
      company: { select: 'id,name,commercialName,shortCode' },
      position: { select: 'id,name' },
      defaultEnvironment: { select: 'id,name' },
    },
  };

  user = await request('/user/info', { params: toQuery(params) });
  let parsedRoles = user.roles;
  if (!Array.isArray(parsedRoles)) {
    try {
      parsedRoles = JSON.parse(parsedRoles || '[]');
    } catch {
      parsedRoles = [];
    }
  }
  user = { ...user, roles: parsedRoles };
  setSessionItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user));

  return user;
}

export async function queryNotices() {
  return request('/api/notices');
}

export async function recoveryPassword(email) {
  return request('/auth/recoveryPassword', {
    method: 'POST',
    data: { email },
  });
}

export async function updatePassword(data) {
  return request.post('/auth/resetPassword', { data });
}

export async function updateProfilePhoto(content) {
  return request('/account/updateProfilePhoto', {
    method: 'POST',
    data: { content },
  });
}
